// components/Layout/Footer.tsx
"use client";

import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import {
  LinkedIn,
  Email,
  Phone,
  LocationOn,
  Instagram,
  YouTube,
  Facebook,
} from "@mui/icons-material";
import Image from "next/image";

const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: "Company",
      links: [
        { label: "About Us", href: "/about" },
        // { label: "Team", href: "/about/team" },
        { label: "Careers", href: "/about/careers" },
        { label: "Partners", href: "/about/partners" },
      ],
    },
    {
      title: "Services",
      links: [
        { label: "Web Development", href: "/services/web-development" },
        { label: "Automation & AI", href: "/services/automation" },
        { label: "IT Support", href: "/services/it-support" },
      ],
    },
    {
      title: "Industries",
      links: [
        { label: "SaaS & Platforms", href: "/industries/saas-platforms" },
        { label: "E-commerce & Retail", href: "/industries/ecommerce" },
        { label: "FinTech & Payments", href: "/industries/fintech" },
        { label: "SMEs & Startups", href: "/industries/smes-startups" },
        { label: "Agencies", href: "/industries/agencies" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Blog", href: "/resources/blog" },
        { label: "Whitepapers & Guides", href: "/resources/whitepapers" },
        { label: "Tools & Templates", href: "/resources/tools" },
        { label: "Case Studies", href: "/case-studies" },
        { label: "Portfolio", href: "/portfolio" },
      ],
    },
  ];

  const contactInfo = [
    {
      icon: <Email sx={{ fontSize: 18 }} />,
      text: "info@linxarlabs.com",
      href: "mailto:info@linxarlabs.com",
    },
    {
      icon: <Phone sx={{ fontSize: 18 }} />,
      text: "+233 20 033 5078",
      href: "tel:+233200335078",
    },
    {
      icon: <LocationOn sx={{ fontSize: 18 }} />,
      text: "Accra, Ghana (HQ)",
      href: "#",
    },
    // {
    //   icon: <LocationOn sx={{ fontSize: 18 }} />,
    //   text: "Amsterdam, Netherlands",
    //   href: "#",
    // },
  ];

  const socialLinks = [
    {
      icon: <LinkedIn sx={{ fontSize: 24 }} aria-label="LinkedIn" />,
      href: "https://www.linkedin.com/company/linxar-labs/",
      label: "LinkedIn",
    },
    {
      icon: <Instagram sx={{ fontSize: 24 }} aria-label="Instagram" />,
      href: "https://www.instagram.com/linxarlabs/",
    },
    {
      icon: <Facebook sx={{ fontSize: 24 }} aria-label="Facebook" />,
      href: "https://facebook.com/LinXarLabs",
    },
    {
      icon: <YouTube sx={{ fontSize: 24 }} aria-label="YouTube" />,
      href: "https://www.youtube.com/@LinXarLabs",
    },
  ];

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.95)} 100%)`,
        color: "white",
        mt: "auto",
      }}
    >
      {/* Main Footer Content */}
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          {/* Logo and Contact Info */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Image
                src="/linxar-main-logo.svg"
                alt="LinXar Labs"
                width={180}
                height={60}
                style={{
                  height: "60px",
                  width: "auto",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>

            <Typography
              variant="body1"
              sx={{ mb: 3, opacity: 0.9, lineHeight: 1.6 }}
            >
              Transforming businesses through innovative web development,
              automation, and AI solutions. Partner with us to scale your
              digital presence.
            </Typography>

            {/* Contact Information */}
            <Box sx={{ mb: 4 }}>
              {contactInfo.map((item, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <Box sx={{ mr: 2, opacity: 0.8 }}>{item.icon}</Box>
                  <Link
                    href={item.href}
                    sx={{
                      color: "white",
                      opacity: 0.9,
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      "&:hover": {
                        opacity: 1,
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {item.text}
                  </Link>
                </Box>
              ))}
            </Box>

            {/* Social Links */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#fff", 0.1),
                    "&:hover": {
                      backgroundColor: alpha("#fff", 0.2),
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                  aria-label={`Visit our ${social.label} Page`}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Navigation Links */}
          {footerSections.map((section) => (
            <Grid key={section.title} size={{ xs: 6, md: 3, lg: 2 }}>
              <Typography
                variant="h6"
                component={"p"}
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  fontSize: "1.1rem",
                }}
              >
                {section.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {section.links.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    sx={{
                      color: "white",
                      opacity: 0.8,
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        opacity: 1,
                        color: theme.palette.secondary.light,
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Bottom Strip */}
      <Box
        sx={{
          borderTop: `1px solid ${alpha("#fff", 0.2)}`,
          py: 3,
          background: alpha("#000", 0.1),
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ opacity: 0.8, textAlign: { xs: "center", md: "left" } }}
            >
              © {currentYear} LinXar Labs. All rights reserved.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 3,
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Link
                href="/privacy-policy"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": {
                    opacity: 1,
                    textDecoration: "underline",
                  },
                }}
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms-of-service"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": {
                    opacity: 1,
                    textDecoration: "underline",
                  },
                }}
              >
                Terms of Service
              </Link>
              <Link
                href="/contact"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  textDecoration: "none",
                  fontSize: "0.875rem",
                  "&:hover": {
                    opacity: 1,
                    textDecoration: "underline",
                  },
                }}
              >
                Contact Form
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
